import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Toast from '../../../components/Common/Toast';
import {
  getOrdersMock,
  MOCK_MENU,
  MOCK_MOZOS,
  MOCK_ZONAS,
  normalizeOrder,
  setOrdersMock,
} from './mockOperaciones';

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

const MesaDetalle = () => {
  const navigate = useNavigate();
  const { zoneId, mesaId } = useParams();

  const zone = useMemo(() => MOCK_ZONAS.find((z) => String(z.id) === String(zoneId)) || null, [zoneId]);
  const isBar = mesaId == null; // ruta /bar/:zoneId no trae mesaId

  const [orders, setOrders] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const target = useMemo(() => {
    if (!zone) return null;
    if (isBar) {
      return { tipo: 'bar', mesaId: null };
    }
    return { tipo: 'mesa', mesaId: Number(mesaId) };
  }, [zone, isBar, mesaId]);

  const [cart, setCart] = useState([]);
  const [observacion, setObservacion] = useState('');
  const [mozo, setMozo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loaded = getOrdersMock();
    setOrders(loaded);
  }, []);

  const currentOrder = useMemo(() => {
    if (!target) return null;
    const o = orders.find(
      (x) =>
        x.zoneId === Number(zoneId) &&
        x.tipo === target.tipo &&
        (isBar ? true : x.mesaId === target.mesaId) &&
        x.estado === 'abierto'
    );
    return o ? normalizeOrder(o) : null;
  }, [orders, zoneId, target, isBar]);

  const isEntregaPendiente = currentOrder?.produccionEstado === 'entrega_pendiente';

  useEffect(() => {
    if (!target) return;
    if (currentOrder) {
      setCart(currentOrder.items || []);
      setObservacion(currentOrder.observacion || '');
      setMozo(currentOrder.mozo || '');
    } else {
      setCart([]);
      setObservacion('');
      setMozo(MOCK_MOZOS[0]?.nombre || '');
    }
  }, [currentOrder, target]);

  const menuFlat = useMemo(() => {
    const out = [];
    MOCK_MENU.forEach((c) => c.items.forEach((it) => out.push({ ...it, category: c.category })));
    return out;
  }, []);

  const filteredMenu = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    if (!q) return MOCK_MENU;

    // Filtrar por nombre del item o por categoría
    return MOCK_MENU.map((cat) => {
      const items = cat.items.filter((it) => {
        const nombreMatch = String(it.nombre || '').toLowerCase().includes(q);
        const catMatch = String(cat.category || '').toLowerCase().includes(q);
        return nombreMatch || catMatch;
      });
      return { ...cat, items };
    }).filter((cat) => (cat.items || []).length > 0);
  }, [searchQuery]);

  const cartTotals = useMemo(() => {
    const subtotal = cart.reduce((s, it) => s + Number(it.precio) * Number(it.cantidad), 0);
    return { subtotal: round2(subtotal) };
  }, [cart]);

  const addItem = (item) => {
    if (isEntregaPendiente) return;
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.itemId === item.id);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], cantidad: Number(next[idx].cantidad) + 1 };
        return next;
      }
      return [...prev, { itemId: item.id, nombre: item.nombre, precio: item.precio, cantidad: 1 }];
    });
  };

  const updateQty = (itemId, qty) => {
    if (isEntregaPendiente) return;
    const q = Math.max(0, Number(qty));
    setCart((prev) => {
      const next = prev
        .map((it) => (it.itemId === itemId ? { ...it, cantidad: q } : it))
        .filter((it) => Number(it.cantidad) > 0);
      return next;
    });
  };

  const entregarPedido = () => {
    if (!currentOrder || currentOrder.produccionEstado !== 'entrega_pendiente') return;
    const fechaEntregaMesa = new Date().toISOString();
    const next = orders.map((o) =>
      o.id === currentOrder.id
        ? normalizeOrder({
            ...o,
            estado: 'cerrado',
            produccionEstado: 'entregado',
            ordenProduccion: o.ordenProduccion
              ? { ...o.ordenProduccion, fechaEntregaMesa }
              : { fechaEntregaMesa, pedidoId: o.id },
          })
        : o
    );
    setOrders(next);
    setOrdersMock(next);
    setToast({ isOpen: true, message: 'Pedido entregado en mesa (demo).', type: 'success' });
  };

  const saveOrder = () => {
    if (!target || !zone) return;
    if (isEntregaPendiente) return;
    if (cart.length === 0) {
      setToast({ isOpen: true, message: 'Agrega al menos un item (demo)', type: 'info' });
      return;
    }

    const existingIdx = orders.findIndex(
      (o) =>
        o.zoneId === Number(zoneId) &&
        o.tipo === target.tipo &&
        (isBar ? true : o.mesaId === target.mesaId) &&
        o.estado === 'abierto'
    );

    const next = orders.slice();
    const prev = existingIdx >= 0 ? next[existingIdx] : null;
    const payload = normalizeOrder({
      id: existingIdx >= 0 ? next[existingIdx].id : `ord-${Date.now()}`,
      zoneId: Number(zoneId),
      tipo: target.tipo,
      mesaId: isBar ? null : target.mesaId,
      estado: 'abierto',
      createdAt: existingIdx >= 0 ? next[existingIdx].createdAt : new Date().toISOString(),
      items: cart.map((it) => ({ itemId: it.itemId, nombre: it.nombre, precio: it.precio, cantidad: it.cantidad })),
      observacion: observacion || '',
      mozo: mozo || '',
      produccionEstado: prev?.produccionEstado || 'pendiente',
      ordenProduccion: prev?.ordenProduccion ?? null,
    });

    if (existingIdx >= 0) next[existingIdx] = payload;
    else next.push(payload);

    setOrders(next);
    setOrdersMock(next);
    setToast({ isOpen: true, message: 'Pedido guardado (demo)', type: 'success' });
  };

  const headerLabel = useMemo(() => {
    if (!zone) return '-';
    if (isBar) return `Bar - ${zone.nombreZona}`;
    return `Mesa ${mesaId} - ${zone.nombreZona}`;
  }, [zone, isBar, mesaId]);

  if (!zone) {
    return (
      <div className="p-6 max-w-5xl mx-auto text-red-600 font-medium">Zona no encontrada</div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{headerLabel}</h1>
          <p className="text-gray-600 text-sm mt-1">
            {isEntregaPendiente ? (
              <>
                Pedido listo desde cocina: <span className="font-medium">entrega pendiente</span>. Revisa el detalle y
                confirma la entrega en mesa.
              </>
            ) : (
              <>
                Agrega items del menú y guarda el pedido (demo). Desde aquí también puedes ir a “Pedidos”.
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
            onClick={() => navigate('/restaurantes/operaciones/pedidos')}
          >
            Ver pedidos
          </button>
          {isEntregaPendiente ? (
            <button
              type="button"
              className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700"
              onClick={entregarPedido}
            >
              Entregar pedido
            </button>
          ) : (
            <button
              type="button"
              className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700"
              onClick={saveOrder}
            >
              Guardar pedido
            </button>
          )}
        </div>
      </div>

      {isEntregaPendiente && currentOrder?.ordenProduccion?.id ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <span className="font-semibold">Orden de producción:</span>{' '}
          <span className="font-mono">{currentOrder.ordenProduccion.id}</span>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-800">Cart / Pedido</h2>
            {isEntregaPendiente ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
                Entrega pendiente
              </span>
            ) : null}
          </div>
          <div className="p-4">
            {cart.length === 0 ? (
              <div className="text-sm text-gray-500">Sin items aún. Usa el menú a la derecha.</div>
            ) : (
              <div className="space-y-3">
                {cart.map((it) => (
                  <div key={it.itemId} className="flex items-center justify-between gap-3 border border-gray-100 rounded-md p-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{it.nombre}</div>
                      <div className="text-xs text-gray-500">Precio: S/ {Number(it.precio).toFixed(2)}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isEntregaPendiente ? (
                        <span className="text-sm font-medium text-gray-800 w-16 text-right">× {it.cantidad}</span>
                      ) : (
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={it.cantidad}
                          onChange={(e) => updateQty(it.itemId, e.target.value)}
                          className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                        />
                      )}
                      <div className="text-sm font-semibold text-teal-800">
                        S/ {(Number(it.precio) * Number(it.cantidad)).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
                <div className="border-t border-gray-200 pt-3 mt-2 flex items-center justify-between">
                  <div className="text-sm text-gray-600 font-medium">Subtotal</div>
                  <div className="text-lg font-bold text-teal-800">S/ {cartTotals.subtotal.toFixed(2)}</div>
                </div>
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Mozo</label>
              <select
                value={mozo}
                onChange={(e) => setMozo(e.target.value)}
                disabled={isEntregaPendiente}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-700"
              >
                <option value="">Sin asignar</option>
                {MOCK_MOZOS.map((m) => (
                  <option key={m.id} value={m.nombre}>
                    {m.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Observación (demo)</label>
              <textarea
                value={observacion}
                onChange={(e) => setObservacion(e.target.value)}
                readOnly={isEntregaPendiente}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm read-only:bg-gray-50 read-only:text-gray-800"
                rows={3}
                placeholder="Ej. sin hielo, preferencia de cocción..."
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h2 className="font-semibold text-gray-800">Menú</h2>
          </div>
          <div className="p-4">
            {isEntregaPendiente ? (
              <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/50 px-4 py-8 text-center text-sm text-amber-950">
                <p className="font-medium">Pedido en entrega pendiente</p>
                <p className="mt-2 text-amber-800/90">
                  No se pueden agregar ítems hasta registrar la entrega con el botón <span className="font-semibold">Entregar pedido</span>.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buscar recetas</label>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ej. pollo, lomo, bebida..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-200"
                  />
                  {searchQuery.trim() ? (
                    <div className="mt-2 text-xs text-gray-500">
                      Mostrando resultados para: <span className="font-medium">{searchQuery.trim()}</span>
                    </div>
                  ) : null}
                </div>

                {filteredMenu.map((cat) => (
                  <div key={cat.category} className="mb-5">
                    <div className="text-sm font-semibold text-teal-800 mb-2">{cat.category}</div>
                    <div className="space-y-2">
                      {cat.items.map((it) => (
                        <div key={it.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-md p-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{it.nombre}</div>
                            <div className="text-xs text-gray-500">S/ {it.precio.toFixed(2)}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => addItem(it)}
                            className="px-3 py-2 bg-teal-50 border border-teal-200 text-teal-800 rounded-md text-sm font-medium hover:bg-teal-100"
                          >
                            + Agregar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {filteredMenu.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-8">No hay recetas que coincidan.</div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default MesaDetalle;

